import {useCallback, useEffect, useMemo, useState} from "react";
import {addMilliseconds, format} from "date-fns";
import {useIntl} from "react-intl";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
dayjs.extend(duration);

import {notify} from "@housebets/housebets-x-package";
import {TRewardType} from "@housebets/housebets-x-package/lib/components/feature/rewards/RewardHorizontal/types";
import {getCryptoFullName, getCurrencyIcon, getTomorrowAt0800GMT} from "app/utils/help";
import {refreshCashBackData, refreshRealCashBackData} from "app/store/slices/user";
import {CurrencyType, MoneyType, TKey} from "app/types/common";
import {useAppSelector} from "hooks/useAppSelector";
import apiAuth from "app/api/auth";
import {IVipReloadProps} from "app/components/features/legacy/VipReload/types";
import {useGTMEvents} from "app/hooks/useGTMEvents";
import { useDispatch } from "react-redux";

interface VipData {
    monthAmount: number;
    monthLoseBonusPrepared: number;
    monthTime: number;
    weekAmount: number;
    weekLoseBonusPrepared: number;
    weekTime: number;
    manualBonusDataVo: {
        bonusId: number;
        count: number;
        leftTime: number;
        num: number;
        tokenId: number;
        details: Array<{rid: number; startTime?: null | number | string; endTime?: null | number | string}>;
    };
}

interface DetailItem {
    amount: number;
    bonusId: number;
    leftTime: number;
    num: number;
    rid: number;
    status: number;
    tokenId: number;
}

type TOnClaimSuccess = ((isDisabledBackMenu?: boolean, closeMenu?: boolean) => void) | null;

const responseText: {
    [key: number]: {
        type: "success" | "error";
        title: string;
    };
} = {
    0: {
        type: "success",
        title: "REWARDS_RESPONSE_CODE_0",
    },
    1: {
        type: "error",
        title: "REWARDS_RESPONSE_CODE_1",
    },
    2: {
        type: "error",
        title: "REWARDS_RESPONSE_CODE_2",
    },
    3: {
        type: "error",
        title: "REWARDS_RESPONSE_CODE_3",
    },
    4: {
        type: "error",
        title: "REWARDS_RESPONSE_CODE_4",
    },
    5: {
        type: "error",
        title: "REWARDS_RESPONSE_CODE_5",
    },
    6: {
        type: "error",
        title: "REWARDS_RESPONSE_CODE_6",
    },
    7: {
        type: "error",
        title: "REWARDS_RESPONSE_CODE_7",
    },
};

export const useRewardListData = (onClaimSuccess: TOnClaimSuccess) => {
    const cashBackDataAmount = useAppSelector((state) => state.user.cashBackDataAmount);
    const cashbackDataRealAmount = useAppSelector((state) => state.user.cashBackDataRealAmount);
    const currencyList = useAppSelector((state) => state.config.platformConfig?.currencyList || []);
    const isLogin = useAppSelector((state) => state.user.isLogin);
    const level = useAppSelector((state) => state.user.user?.level || 0);
    const vipList = useAppSelector((state) => state.config.platformConfig?.vipList || []);
    const token = useAppSelector((state) => state.user.token);
    const {bonus} = useGTMEvents();
    const [isLoading, setIsLoading] = useState(false);
    const [isOpenModal1, setIsOpenModal1] = useState(false);
    const [modal2Type, setModal2Type] = useState<"daily" | "week" | "month" | "instant" | "level">("week");
    const [isOpenModal2, setIsOpenModal2] = useState(false);
    const [id, setId] = useState<TKey[]>([MoneyType.USDT]);
    const dispatch = useDispatch();
    const [vipData, setVipData] = useState<VipData | null>(Object.create(null));
    const [vipUpData, setVipUpData] = useState<
        {
            claimStatus: number;
            ctime: number;
            level: number;
            levelAmount: number;
            platformId: number;
            rid: number;
            tokenId: number;
            userId: number;
            utime: number;
        }[]
    >([]);
    const [instantClaimed, setInstantClaimed] = useState(false);
    const [isLevelUpClaimed, setIsLevelUpClaimed] = useState(false);

    const getClaimRemainTitle = (prepare: boolean, time: number | string | Date, defaultText: string) => {
        if (!prepare) return defaultText;

        const toFormat = typeof time === "number" ? format(addMilliseconds(new Date(), time || 0), "yyyy-MM-dd HH:mm:ss") : time;
        const targetDate = dayjs(toFormat);
        const now = dayjs();

        const daysDiff = targetDate.diff(now, "day");
        const hoursDiff = targetDate.diff(now, "hour");
        const minutesDiff = targetDate.diff(now, "minutes");
        const secondDiff = targetDate.diff(now, "minutes");

        const startText =
            daysDiff > 0
                ? `${daysDiff} ${$t({id: "days"})}`
                : hoursDiff > 0
                  ? `${hoursDiff} ${$t({id: "hours"})}`
                  : minutesDiff > 0
                    ? `${minutesDiff} ${$t({id: "minutes"})}`
                    : `${secondDiff} ${$t({id: "seconds"})}`;
        const endText = $t({id: "RemainingToClaim"});

        return `${startText} ${endText}`;
    };

    const VipReloadData: IVipReloadProps = {
        title: "Choose the currency you would like to be paid out in:",
        total: {
            amount: "00.00",
            currency: 1,
        },
        claims: {
            currentLevel: 1,
            maxLevels: 1,
            list: [],
        },
        onClick: () => {},
    };

    const [isCode1, setIsCode1] = useState(false);
    const [vipReloadData, setVipReloadData] = useState(VipReloadData);
    const [vipReloadModalOpen, setVipReloadModalOpen] = useState(false);
    const [isLoadingReceive, setIsLoadingReceive] = useState(false);

    const {$t} = useIntl();

    const receiveManualBonus = useCallback(async () => {
        if (isLoadingReceive) return;
        setIsLoadingReceive(true);
        try {
            const bonusId = isCode1 ? 0 : vipData?.manualBonusDataVo.bonusId;
            const rid = vipReloadData.claims.list.find((v) => v.level === vipReloadData.claims.currentLevel)?.rid;
            const payLoad = {
                bonusId,
                rid,
            };
            const res = await apiAuth.receiveManualBonus(payLoad);
            if (res.status === 200 && res.data.code === 0) {
                if (vipReloadData.claims.list.length > 1) {
                    const claims = {
                        currentLevel: vipReloadData.claims.currentLevel + 1,
                        maxLevels: vipReloadData.claims.maxLevels,
                        list: vipReloadData.claims.list.map((item, index) => {
                            if (index === vipReloadData.claims.currentLevel - 1) {
                                return {
                                    ...item,
                                    level: 2,
                                };
                            }
                            return {
                                ...item,
                            };
                        }),
                        bonusId: vipData?.manualBonusDataVo.bonusId,
                    };

                    setVipReloadData({
                        ...vipReloadData,
                        claims,
                    });
                }

                getManualBonusData(0);
                bonus.claimSuccess({
                    bonus_code: `Vip Reload`,
                    bonus_type: `Vip Reload`,
                    additional_parameters: {
                        bonusId,
                        rid,
                        amount: vipReloadData.total,
                        currency: getCryptoFullName(id[0].toString()),
                    },
                });

                const oldVipData = vipReloadData.claims.list;

                getVipMonthData((vipData) => {
                    if (onClaimSuccess && oldVipData.length > 1) {
                        const isDisabledBackMenu =
                            (vipData?.manualBonusDataVo?.details?.length ?? 0) > 1 &&
                            vipData?.manualBonusDataVo?.count !== 0 &&
                            (vipData?.manualBonusDataVo?.count ?? 0) + 1 !== (vipData?.manualBonusDataVo?.num ?? 0);
                        onClaimSuccess(!isDisabledBackMenu, true);
                    } else if (onClaimSuccess) {
                        onClaimSuccess();
                    }
                });

                return;
            }
            notify({
                type: "error",
                title: `receiveManualBonusError_${res.data.code}`,
            });
        } finally {
            setIsLoadingReceive(false);
        }
    }, [vipData, vipReloadData, isLoadingReceive]);

    const getVipMonthData = async (cb?: (e: any) => void) => {
        const {status, data} = await apiAuth.getVipMonthData();
        if (status === 200 && data.code === 0) {
            setVipData(data.data);

            if (cb) {
                cb(data.data);
            }
        }
    };
    const getManualBonusData = async (bonusId?: number) => {
        if (bonusId !== undefined && !isCode1) {
            setIsCode1(true);
        }
        setIsLoading(true);
        const payLoad = {bonusId: isCode1 || bonusId !== undefined ? 0 : vipData?.manualBonusDataVo.bonusId};
        const res = await apiAuth.getManualBonus(payLoad);
        if (res.status === 200) {
            if (res.data.code === 0) {
                const data = res.data.data;
                const total = {
                    amount: data.amount + "",
                    currency: data.tokenId,
                };
                const details = data.details || [];

                const list = details.map((v: DetailItem) => {
                    return {
                        amount: v.amount,
                        level: v.num,
                        status: v.status,
                        leftTime: v.leftTime,
                        rid: v.rid,
                    };
                });
                const maxLevels = details[details.length - 1]?.num;

                const currentLevel = data.num;
                const claims = {
                    currentLevel: currentLevel,
                    maxLevels: maxLevels,
                    list,
                    bonusId: vipData?.manualBonusDataVo.bonusId,
                };

                setVipReloadData({
                    ...vipReloadData,
                    total,
                    claims,
                });
                if (details.length === 0) {
                    setVipReloadModalOpen(false);
                } else {
                    setVipReloadModalOpen(true);
                }
            } else {
                setVipReloadModalOpen(false);
                getVipMonthData();
                getManualBonusData(0);
            }
        }
        setIsLoading(false);
    };

    const queryVipLevelUpData = async () => {
        const {status, data} = await apiAuth.queryVipLevelUpData();
        if (status === 200 && data.code === 0) {
            setVipUpData(data.data);
        }
    };

    const getVipLevelUpReward = async () => {
        setIsLoading(true);
        const {status, data} = await apiAuth.getVipLevelUpReward({level: level});
        if (status === 200 && data.code === 0) {
            if (onClaimSuccess) {
                onClaimSuccess();
                setIsLevelUpClaimed(true);
            } else {
                notify({
                    type: "success",
                    title: $t({id: "ClaimSuccess"}),
                });
            }
            bonus.claimSuccess({
                bonus_code: `Level Up`,
                bonus_type: `Level Up`,
                additional_parameters: {
                    level,
                },
            });
            queryVipLevelUpData();
        } else {
            notify({
                type: responseText[data.code]?.type || "error",
                title: $t({id: responseText[data.code]?.title || "REWARDS_RESPONSE_SYSTEM_ERROR"}),
            });
        }
        setIsLoading(false);
    };

    const vipUpDataDict = useMemo(() => {
        return vipUpData.reduce((pre, cur) => {
            pre[cur.level] = cur;
            return pre;
        }, Object.create(null));
    }, [vipUpData]);

    const lowLevel = useMemo(() => {
        const lowLevel = [-1, -1];
        vipList.forEach((v) => {
            if (v.vipWeekReward > 0 && lowLevel[0] === -1) {
                lowLevel[0] = v.level;
            }
            if (v.vipMonthReward > 0 && lowLevel[1] === -1) {
                lowLevel[1] = v.level;
            }
        });
        if (lowLevel[0] === -1) lowLevel[0] = 0;
        if (lowLevel[1] === -1) lowLevel[1] = 0;
        return lowLevel[0];
    }, []);

    const formatTimeLeft = (days: number, hours: number, minutes: number, seconds: number): string => {
        if (days > 0) {
            return $t({id: "timeLeft.days"}, {count: days});
        } else if (hours > 0) {
            return $t({id: "timeLeft.hours"}, {count: hours});
        } else if (minutes > 0) {
            return $t({id: "timeLeft.minutes"}, {count: minutes});
        } else {
            return $t({id: "timeLeft.seconds"}, {count: seconds});
        }
    };

    const calculateTimeUntilNextDay = () => {
        const now = dayjs();
        const nextDay = now.add(1, "day").startOf("day").add(0, "hour");
        const diff = nextDay.diff(now);
        const d = dayjs.duration(diff);
        const days = Math.floor(d.asDays());
        const hours = d.hours();
        const minutes = d.minutes();
        const seconds = d.seconds();
        if (diff <= 0) {
            return "";
        }
        return formatTimeLeft(days, hours, minutes, seconds);
    };

    const [timeLeftDailyReload, setTimeLeftDailyReload] = useState<string>("");

    const DailyData = useMemo(() => {
        const isAuth = isLogin;
        const prepared = cashBackDataAmount !== 0;
        const enableTomorrowAt = getTomorrowAt0800GMT();

        return {
            category: prepared ? timeLeftDailyReload : getClaimRemainTitle(prepared, enableTomorrowAt, ""),
            name: $t({id: "vipLevels.daily.title"}),
            isAuth: isAuth,
            enableAt: "",
            prepared: prepared,
            isAvailable: prepared,
            notAvailable: !prepared,

            // notAvailable: false,
            // category: timeLeftDailyReload,

            aviableText: "",
            completed: false,
            claimed: false,
            imageCompleted: "",
            image: "daily_rakeback",
            onClick: () => {
                setModal2Type("daily");
                setId([MoneyType.USDT]);
                setIsOpenModal2(true);
                bonus.claim({
                    bonus_code: "Daily Rakeback",
                    bonus_type: "Daily Rakeback",
                });
            },
            canClaim: prepared,
        };
    }, [$t, isLogin, cashBackDataAmount, timeLeftDailyReload]);

    useEffect(() => {
        let intervalDailyReload: ReturnType<typeof setInterval> | undefined;
        if (DailyData?.notAvailable === false) {
            clearInterval(intervalDailyReload);
            intervalDailyReload = setInterval(() => {
                setTimeLeftDailyReload(calculateTimeUntilNextDay());
            }, 1000);
        } else {
            clearInterval(intervalDailyReload);
        }
        return () => clearInterval(intervalDailyReload);
    }, [DailyData]);

    const [timeLeftWeeklyReload, setTimeLeftWeeklyReload] = useState<string>("");

    const calculateTimeUntilThursday = () => {
        const now = dayjs();
        let nextThursday = dayjs().day(4).startOf("day");
        if (now.isAfter(nextThursday)) {
            nextThursday = nextThursday.add(7, "day");
        }
        const diff = nextThursday.diff(now);
        const d = dayjs.duration(diff);
        const days = Math.floor(d.asDays());
        const hours = d.hours();
        const minutes = d.minutes();
        const seconds = d.seconds();
        if (diff <= 0) {
            return "";
        }
        return formatTimeLeft(days, hours, minutes, seconds);
    };

    const WeeklyData = useMemo(() => {
        const claimed = false;
        const completed = false;

        const isAuth = isLogin;
        const prepared = vipData?.weekLoseBonusPrepared === 1;
        const aviable = level >= lowLevel;
        const aviableText = `${$t({id: "AviableAtVip"})} ${lowLevel}`;
        return {
            category: !(!prepared || !aviable) ? timeLeftWeeklyReload : $t({id: "HousebetsVIPProgram"}),
            name: $t({id: "WeeklyReload"}),
            isAuth: isAuth,
            enableAt: "",
            prepared: prepared,
            isAvailable: aviable,
            aviableText: aviableText,
            notAvailable: !prepared || !aviable,

            count: vipData?.weekLoseBonusPrepared,

            // notAvailable: false,
            // category: timeLeftWeeklyReload,

            completed: completed,
            claimed: claimed,
            imageCompleted: "",
            image: "week_reload",
            onClick: () => {
                setModal2Type("week");
                setId([MoneyType.USDT]);
                setIsOpenModal2(true);
                bonus.claim({
                    bonus_code: "Weekly Reload",
                    bonus_type: "Weekly Reload",
                });
            },
            canClaim: prepared,
        };
    }, [$t, isLogin, vipData, timeLeftWeeklyReload]);

    useEffect(() => {
        let intervalWeeklyReload: ReturnType<typeof setInterval> | undefined;
        if (WeeklyData?.notAvailable === false) {
            clearInterval(intervalWeeklyReload);
            intervalWeeklyReload = setInterval(() => {
                setTimeLeftWeeklyReload(calculateTimeUntilThursday());
            }, 1000);
        } else {
            clearInterval(intervalWeeklyReload);
        }

        return () => clearInterval(intervalWeeklyReload);
    }, [WeeklyData]);

    const [timeLeftMonthReload, setTimeLeftMonthReload] = useState<string>("");

    const calculateTimeUntilNextMonth = () => {
        const now = dayjs();
        const nextMonth = now.add(1, "month").startOf("month");
        const diff = nextMonth.diff(now);
        const d = dayjs.duration(diff);
        const days = Math.floor(d.asDays());
        const hours = d.hours();
        const minutes = d.minutes();
        const seconds = d.seconds();
        if (diff <= 0) {
            return "";
        }
        return formatTimeLeft(days, hours, minutes, seconds);
    };

    const MonthData = useMemo(() => {
        const claimed = false;
        const completed = false;

        const isAuth = isLogin;
        const prepared = vipData?.monthLoseBonusPrepared === 1;
        const aviable = level >= lowLevel;
        const aviableText = `${$t({id: "AviableAtVip"})} ${lowLevel}`;

        return {
            category: getClaimRemainTitle(prepared, vipData?.monthTime || 0, $t({id: "HousebetsVIPProgram"})),
            name: $t({id: "MonthlyReload"}),
            isAuth: isAuth,
            enableAt: "",
            prepared: prepared,
            notAvailable: !aviable || !prepared,

            // notAvailable: false,
            // category: timeLeftMonthReload,

            aviableText: aviableText,
            completed: completed,
            claimed: claimed,
            imageCompleted: "",
            image: "month_reload",
            onClick: () => {
                setModal2Type("month");
                setId([MoneyType.USDT]);
                setIsOpenModal2(true);
                bonus.claim({
                    bonus_code: "Monthly Reload",
                    bonus_type: "Monthly Reload",
                });
            },
            canClaim: prepared,
        };
    }, [$t, isLogin, vipData, timeLeftMonthReload]);

    useEffect(() => {
        let intervalMonthReload: ReturnType<typeof setInterval> | undefined;
        if (MonthData?.notAvailable === false) {
            clearInterval(intervalMonthReload);
            intervalMonthReload = setInterval(() => {
                setTimeLeftMonthReload(calculateTimeUntilNextMonth());
            }, 1000);
        } else {
            clearInterval(intervalMonthReload);
        }
        return () => clearInterval(intervalMonthReload);
    }, [MonthData]);

    // new 2

    const calculateTimeUntilTime = (start: any, finish: string | number | Date): string => {
        const diff: number = dayjs(finish).diff(dayjs(start));

        const d = dayjs.duration(diff);
        const days: number = Math.floor(d.asDays());
        const hours: number = d.hours();
        const minutes: number = d.minutes();
        const seconds: number = d.seconds();
        // The original code returns a string, but the function signature expects an object.
        // Adjusting to return the correct type.
        return diff <= 0 ? "" : formatTimeLeft(days, hours, minutes, seconds);
    };

    const [timeReloadLeft, setTimeReloadLeft] = useState<string>("");
    const ReloadData = useMemo(() => {
        const manualBonusDataVoCount = vipData?.manualBonusDataVo?.count || 0;
        const claimed = false;
        const completed = false;

        const isAuth = isLogin;
        const prepared = manualBonusDataVoCount > 0;
        const aviable = manualBonusDataVoCount > 0;
        const aviableText = $t({id: "Claim"});
        const type: TRewardType = manualBonusDataVoCount > 0 ? "remaining" : "info";

        return {
            category: manualBonusDataVoCount <= 1 ? timeReloadLeft : `${manualBonusDataVoCount} remaining`,
            manualBonusDataVoCount: manualBonusDataVoCount,
            name: $t({id: "VIPReload"}),
            isAuth: isAuth,
            enableAt: "",
            prepared: prepared,
            notAvailable: !aviable,
            aviableText: aviableText,
            completed: completed,
            claimed: claimed,
            imageCompleted: "",
            image: "vip_reload",
            type: type,
            onClick: () => {
                setIsCode1(false);
                getManualBonusData();
                bonus.claim({
                    bonus_code: "Vip Reload",
                    bonus_type: "Vip Reload",
                });
            },
            canClaim: aviable,
        };
    }, [$t, isLogin, vipData, timeReloadLeft]);

    useEffect(() => {
        const manualBonusDataVoCount = vipData?.manualBonusDataVo?.details || [];

        let intervalReloadData: ReturnType<typeof setInterval> | undefined;

        if (manualBonusDataVoCount.length === 1 && manualBonusDataVoCount[0]?.endTime && manualBonusDataVoCount[0]?.endTime !== null) {
            clearInterval(intervalReloadData);
            intervalReloadData = setInterval(() => {
                const endTime = manualBonusDataVoCount[0]?.endTime;
                if (endTime !== undefined && endTime !== null) {
                    setTimeReloadLeft(calculateTimeUntilTime(new Date(), endTime));
                }
            }, 1000);
        } else {
            clearInterval(intervalReloadData);
        }
        return () => clearInterval(intervalReloadData);
    }, [vipData]);

    const LevelUpData = useMemo(() => {
        const isAuth = isLogin;
        const prepared = !vipUpDataDict[level] ? false : vipUpDataDict[level].claimStatus ? false : true;

        return {
            category: "Vip Program",
            name: $t({id: "LevelUp"}),
            isAuth: isAuth,
            enableAt: "",
            prepared: prepared,
            notAvailable: !prepared,
            aviableText: "",
            completed: false,
            claimed: isLevelUpClaimed,
            imageCompleted: "",
            image: "vip_reload",
            onClick: () => {
                bonus.claim({
                    bonus_code: "Level Up",
                    bonus_type: "Level Up",
                });
                setModal2Type("level");
                setId([MoneyType.USDT]);
                setIsOpenModal2(true);
            },
            canClaim: prepared,
        };
    }, [$t, isLogin, vipUpDataDict, level, isLevelUpClaimed]);

    const InstantData = useMemo(() => {
        const isAuth = isLogin;
        const aviableText = $t({id: "Claim"});
        const prepared = cashbackDataRealAmount > 0;
        const aviable = cashbackDataRealAmount > 0;

        return {
            category: "",
            name: $t({id: "vipLevels.instant.title"}),
            isAuth: isAuth,
            enableAt: "",
            prepared: prepared,
            aviable: aviable,
            cashbackDataRealAmount: cashbackDataRealAmount,
            notAvailable: !prepared,
            aviableText: aviableText,
            completed: false,
            claimed: instantClaimed,
            imageCompleted: "",
            image: "instant_rakeback",
            onClick: () => {
                setModal2Type("instant");
                setId([MoneyType.USDT]);
                setIsOpenModal2(true);
                bonus.claim({
                    bonus_code: "Instant Rakeback",
                    bonus_type: "Instant Rakeback",
                });
            },
            canClaim: prepared,
        };
    }, [$t, isLogin, instantClaimed, cashbackDataRealAmount]);

    const ClaimData = useMemo(() => {
        // TODO: temp filter
        const res = currencyList
            .filter((v) => v.categoryId === CurrencyType.Crypto || v.categoryId === CurrencyType.Fiat)
            .filter((item) => item.tokenId === MoneyType.USDT);
        const list = res.map((v) => {
            return {
                id: v.tokenId,
                name: getCryptoFullName(v.tokenName),
                icon: getCurrencyIcon(v.tokenId),
            };
        });

        return {
            title: "The redeemed currency will be added to your account",
            image: "",
            list: list,
            total: {
                amount:
                    modal2Type === "daily"
                        ? (cashBackDataAmount || 0).toFixed(7) + ""
                        : modal2Type === "week"
                          ? (vipData?.weekAmount || 0).toFixed(7) + ""
                          : modal2Type === "month"
                            ? (vipData?.monthAmount || 0).toFixed(7) + ""
                            : modal2Type === "instant"
                              ? cashbackDataRealAmount.toFixed(7) + ""
                              : modal2Type === "level"
                                ? vipUpDataDict?.[level]?.levelAmount || 0
                                : 0,
                currency: id[0] + "",
                currencyIcon: getCurrencyIcon(id[0]),
            },
        };
    }, [$t, currencyList, id, modal2Type, vipData, cashBackDataAmount, cashbackDataRealAmount]);

    const dropdownRewardList = useMemo(() => {
        return [
            {
                item_type: "instant_rakeback",
                ...InstantData,
            },
            {
                item_type: "level_up",
                ...LevelUpData,
            },
            {
                item_type: "vip_reload",
                ...ReloadData,
            },
            {
                item_type: "daily_reload",
                ...DailyData,
            },
            {
                item_type: "weekly_reload",
                ...WeeklyData,
            },
            {
                item_type: "monthly_reload",
                ...MonthData,
            },
        ].sort((a, b) => {
            if (a.canClaim && !b.canClaim) {
                return -1;
            } else if (!a.canClaim && b.canClaim) {
                return 1;
            }

            return 0;
        });
    }, [InstantData, DailyData, WeeklyData, MonthData, LevelUpData, ReloadData]);

    const isSomeAvailable = useMemo(() => {
        return (
            InstantData.canClaim ||
            DailyData.canClaim ||
            (WeeklyData.canClaim && level >= 7) ||
            (LevelUpData.canClaim && level >= 7) ||
            (ReloadData.canClaim && level >= 7) ||
            (MonthData.canClaim && level >= 17)
        );
    }, [InstantData, DailyData, WeeklyData, MonthData, LevelUpData, ReloadData, level]);

    const claimHandler = async () => {
        setIsLoading(true);
        if (modal2Type === "instant" || modal2Type === "daily") {
            const extraData = modal2Type === "instant" ? {amount: cashbackDataRealAmount} : {}; // cashBackDataRealAmount
            const {status, data} = await apiAuth.getVipCashbackRewards({tokenId: Number(id[0]), type: modal2Type === "instant" ? 1 : 0, ...extraData});
            if (status === 200 && data.code === 0) {
                if (onClaimSuccess) {
                    onClaimSuccess();
                } else {
                    notify({
                        type: "success",
                        title: "Rakeback received",
                    });
                }
                if (modal2Type === "instant") {
                    setInstantClaimed(true);
                }
                dispatch(refreshCashBackData());
                dispatch(refreshRealCashBackData());
                setIsOpenModal2(false);
                bonus.claimSuccess({
                    bonus_code: `${modal2Type === "instant" ? "Instant" : "Daily"} Rakeback`,
                    bonus_type: `${modal2Type === "instant" ? "Instant" : "Daily"} Rakeback`,
                    additional_parameters: {
                        amount: modal2Type === "instant" ? cashbackDataRealAmount : cashBackDataAmount,
                        currency: getCryptoFullName(id[0].toString()),
                    },
                });
                setIsLoading(false);
                return;
            }
            setIsLoading(false);
            notify({
                type: responseText[data.code]?.type || "error",
                title: $t({id: responseText[data.code]?.title || "REWARDS_RESPONSE_SYSTEM_ERROR"}),
            });
            return;
        }
        if (modal2Type === "level") {
            getVipLevelUpReward();
            return;
        }
        const {status, data} = await apiAuth.getVipMonthRewards(Number(id[0]), modal2Type === "week" ? 0 : 1);
        if (status === 200 && data.code === 0) {
            notify({
                type: "success",
                title: $t({id: "UpdatedSuccess"}),
            });
            bonus.claimSuccess({
                bonus_code: `${modal2Type === "week" ? "Week" : "Month"} Rakeback`,
                bonus_type: `${modal2Type === "week" ? "Week" : "Month"} Rakeback`,
                additional_parameters: {
                    amount: modal2Type === "week" ? vipData?.weekAmount : vipData?.monthAmount,
                    currency: getCryptoFullName(id[0].toString()),
                },
            });
            getVipMonthData();
            setIsOpenModal2(false);
            setIsLoading(false);
            return;
        }
        setIsLoading(false);
        notify({
            type: responseText[data.code]?.type || "error",
            title: $t({id: responseText[data.code]?.title || "REWARDS_RESPONSE_SYSTEM_ERROR"}),
        });
    };

    useEffect(() => {
        if (token) {
            setTimeout(() => {
                getVipMonthData();
            }, 1);
        }
        return () => {
            setVipData(null);
        };
    }, [token]);

    useEffect(() => {
        if (token) {
            queryVipLevelUpData();
        } else {
            setVipUpData([]);
        }
    }, [token]);

    useEffect(() => {
        if (token) {
            dispatch(refreshCashBackData());
            dispatch(refreshRealCashBackData());
        }
    }, [token]);

    return {
        DailyData,
        WeeklyData,
        MonthData,
        ReloadData,
        LevelUpData,
        InstantData,
        isOpenModal1,
        setIsOpenModal1,
        modal2Type,
        isOpenModal2,
        setIsOpenModal2,
        ClaimData,
        id,
        setId,
        vipReloadModalOpen,
        receiveManualBonus,
        setVipReloadModalOpen,
        vipReloadData,
        claimHandler,
        level,
        isLevelUpClaimed,
        isLogin,
        dropdownRewardList,
        isSomeAvailable,
        isLoading: isLoadingReceive || isLoading,
    };
};
